import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

// Utilidades para convertir entre ArrayBuffer y Base64URL
const bufferToBase64URL = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64URLToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const BiometricAuth = () => {
  const { API, user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    checkSupport();
    loadStatus();
  }, []);

  const checkSupport = async () => {
    // Verificar si WebAuthn está soportado
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setIsSupported(available);
      } catch (e) {
        setIsSupported(false);
      }
    } else {
      setIsSupported(false);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/webauthn/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error cargando estado biométrico:', error);
    } finally {
      setLoading(false);
    }
  };

  const registerBiometric = async () => {
    setRegistering(true);
    try {
      // 1. Iniciar registro en el servidor
      const startResponse = await axios.post(`${API}/auth/webauthn/register/start`);
      const options = startResponse.data.options;

      // 2. Convertir challenge y user.id a ArrayBuffer
      options.challenge = base64URLToBuffer(options.challenge);
      options.user.id = base64URLToBuffer(options.user.id);
      
      // Convertir excludeCredentials si existen
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: base64URLToBuffer(cred.id)
        }));
      }

      // 3. Crear credencial con el autenticador del dispositivo
      const credential = await navigator.credentials.create({
        publicKey: options
      });

      // 4. Preparar respuesta para el servidor
      const credentialResponse = {
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON),
          attestationObject: bufferToBase64URL(credential.response.attestationObject),
        },
        device_name: detectDeviceName()
      };

      // 5. Completar registro en el servidor
      const completeResponse = await axios.post(`${API}/auth/webauthn/register/complete`, {
        credential: credentialResponse
      });

      if (completeResponse.data.success) {
        toast.success('🔐 Autenticación biométrica habilitada');
        // Recargar estado para verificar que se guardó
        await loadStatus();
      } else {
        toast.error('Error al guardar la credencial');
      }

    } catch (error) {
      console.error('Error registrando biométrico:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      console.error('Detalle del error:', errorMsg);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Operación cancelada o no permitida');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Tu dispositivo no soporta esta función');
      } else if (error.name === 'SecurityError') {
        toast.error('Error de seguridad. Verifica que estés en HTTPS.');
      } else {
        toast.error(`Error: ${errorMsg}`);
      }
    } finally {
      setRegistering(false);
    }
  };

  const deleteCredential = async (credentialId) => {
    if (!window.confirm('¿Eliminar esta credencial biométrica?')) return;
    
    try {
      await axios.delete(`${API}/auth/webauthn/credential/${encodeURIComponent(credentialId)}`);
      toast.success('Credencial eliminada');
      loadStatus();
    } catch (error) {
      toast.error('Error al eliminar credencial');
    }
  };

  const detectDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone (Face ID/Touch ID)';
    if (/iPad/.test(ua)) return 'iPad (Face ID/Touch ID)';
    if (/Macintosh/.test(ua)) return 'Mac (Touch ID)';
    if (/Android/.test(ua)) return 'Android (Huella/Facial)';
    if (/Windows/.test(ua)) return 'Windows Hello';
    return 'Dispositivo biométrico';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-indigo-100">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardTitle className="flex items-center gap-2 text-lg">
          <i className="fas fa-fingerprint text-indigo-600"></i>
          Acceso Biométrico
          {status?.biometric_enabled && (
            <Badge className="bg-green-500 text-white ml-2">Activo</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {!isSupported ? (
          <div className="text-center py-4">
            <i className="fas fa-exclamation-triangle text-4xl text-yellow-500 mb-3"></i>
            <p className="text-gray-600 mb-2">Tu dispositivo no soporta autenticación biométrica</p>
            <p className="text-sm text-gray-500">
              Necesitas un dispositivo con huella dactilar, Face ID o Windows Hello
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-4">
              Habilita el acceso con huella dactilar o reconocimiento facial para iniciar sesión más rápido.
            </p>

            {/* Credenciales existentes */}
            {status?.credentials && status.credentials.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Dispositivos configurados:</p>
                {status.credentials.map((cred, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <i className="fas fa-mobile-alt text-indigo-600"></i>
                      <div>
                        <p className="font-medium text-sm">{cred.device_name}</p>
                        <p className="text-xs text-gray-500">
                          Configurado: {formatDate(cred.created_at)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteCredential(cred.credential_id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Botón para agregar */}
            <Button
              onClick={registerBiometric}
              disabled={registering}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {registering ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Configurando...
                </>
              ) : status?.biometric_enabled ? (
                <>
                  <i className="fas fa-plus mr-2"></i>
                  Agregar otro dispositivo
                </>
              ) : (
                <>
                  <i className="fas fa-fingerprint mr-2"></i>
                  Habilitar acceso biométrico
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 mt-3 text-center">
              <i className="fas fa-shield-alt mr-1"></i>
              Tus datos biométricos nunca salen de tu dispositivo
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BiometricAuth;
